import React from 'react';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';
import { FILLED_STAR_COLOR } from 'components/core/fractional-star';
import type { ProductRatingSummary } from './use-product-rating-summary';

type RatingDistributionProps = {
  summary: Pick<
    ProductRatingSummary,
    'fiveStarCount' | 'fourStarCount' | 'oneStarCount' | 'reviewCount' | 'threeStarCount' | 'twoStarCount'
  >;
};

/** Per-star breakdown bars, ordered 5 → 1. */
export const RatingDistribution = ({ summary }: RatingDistributionProps): JSX.Element => {
  const { t } = useTranslation();
  const total = summary.reviewCount || 0;
  const rows = [
    { stars: 5, count: summary.fiveStarCount },
    { stars: 4, count: summary.fourStarCount },
    { stars: 3, count: summary.threeStarCount },
    { stars: 2, count: summary.twoStarCount },
    { stars: 1, count: summary.oneStarCount },
  ];

  return (
    <Grid>
      {rows.map(({ stars, count }) => {
        const percent = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <Row key={stars}>
            <Label>{t('product.reviews.star-label', '{{count}} Star', { count: stars })}</Label>
            <Track role='img' aria-label={`${stars} star: ${percent}%`}>
              <Fill style={{ width: `${percent}%` }} />
            </Track>
            <Percent>{percent}%</Percent>
          </Row>
        );
      })}
    </Grid>
  );
};

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr 36px;
  align-items: center;
  gap: 12px;
`;

const Label = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[35]};
  white-space: nowrap;
`;

const Track = styled.span`
  height: 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.grey[90]};
  overflow: hidden;
`;

const Fill = styled.span`
  display: block;
  height: 100%;
  border-radius: 4px;
  background: ${FILLED_STAR_COLOR};
`;

const Percent = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[35]};
  text-align: right;
`;
